import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  X,
  FileText,
  Calendar,
  Building,
  CreditCard,
  DollarSign,
  ArrowUpDown,
  Info,
  Receipt,
  Users,
  Home,
  Zap,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Upload,
  History,
  RefreshCw,
  TrendingUp,
  MapPin
} from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { format } from 'date-fns';
import { useState } from 'react';
import type { PaymentStage } from '../TumPaymentReport';

interface PaymentTaskDetailModalProps {
  taskId: string;
  stage: PaymentStage;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface BillPaymentDetail {
  id: string;
  bill_id: string;
  bill_number?: string;
  due_date: string;
  invoice_date: string;
  vendor_name: string;
  billing_id: string;
  amount_due: number;
  fees: number;
  amount_paid: number;
  payment_status: string;
  customer_name?: string;
  account_number?: string;
  commodity?: string;
  funds_requested_status: 'pending' | 'requested' | 'received';
  funds_received_date?: string;
  payment_execution_status: 'pending' | 'executed' | 'failed' | 'reconciled';
}

export function PaymentTaskDetailModal({ taskId, stage, open, onOpenChange }: PaymentTaskDetailModalProps) {
  const { currentWorkspace } = useWorkspace();
  const [sortField, setSortField] = useState<string>('bill_id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [activeTab, setActiveTab] = useState('bills');

  // Generate dummy bill details for demo
  const dummyBillDetails: BillPaymentDetail[] = [
    {
      id: '1',
      bill_id: '1107769',
      bill_number: '1107769',
      due_date: '2025-12-12',
      invoice_date: '2025-11-21',
      vendor_name: 'Duke Energy Payment Processing',
      billing_id: '9101 2834 0913',
      amount_due: 16.76,
      fees: 0.00,
      amount_paid: 16.76,
      payment_status: 'paid',
      customer_name: 'Customer A',
      account_number: '9101 2834 0913',
      commodity: 'electric',
      funds_requested_status: stage === 'funds-requested' || stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid' ? 'received' : 'pending',
      funds_received_date: stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid' ? '2025-12-10' : undefined,
      payment_execution_status: stage === 'payment-initiated' ? 'executed' : stage === 'paid' ? 'reconciled' : 'pending',
    },
    {
      id: '2',
      bill_id: '1108315',
      bill_number: '1108315',
      due_date: '2025-12-12',
      invoice_date: '2025-11-21',
      vendor_name: 'Duke Energy Payment Processing',
      billing_id: '9101 2835 6292',
      amount_due: 24.42,
      fees: 0.00,
      amount_paid: 24.42,
      payment_status: 'paid',
      customer_name: 'Customer B',
      account_number: '9101 2835 6292',
      commodity: 'electric',
      funds_requested_status: stage === 'funds-requested' || stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid' ? 'received' : 'pending',
      funds_received_date: stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid' ? '2025-12-11' : undefined,
      payment_execution_status: stage === 'payment-initiated' ? 'executed' : stage === 'paid' ? 'reconciled' : 'pending',
    },
    {
      id: '3',
      bill_id: '1110721',
      bill_number: '1110721',
      due_date: '2025-12-12',
      invoice_date: '2025-11-21',
      vendor_name: 'Duke Energy Payment Processing',
      billing_id: '9101 5438 6734',
      amount_due: 97.99,
      fees: 0.00,
      amount_paid: 97.99,
      payment_status: 'paid',
      customer_name: 'Customer C',
      account_number: '9101 5438 6734',
      commodity: 'water',
      funds_requested_status: stage === 'funds-requested' ? 'requested' : stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid' ? 'received' : 'pending',
      funds_received_date: stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid' ? '2025-12-12' : undefined,
      payment_execution_status: stage === 'payment-initiated' ? 'executed' : stage === 'paid' ? 'reconciled' : 'pending',
    },
  ];

  const billDetails = taskId.startsWith('task-') ? dummyBillDetails : [];

  const sortedBills = [...billDetails].sort((a, b) => {
    const aValue = a[sortField as keyof BillPaymentDetail];
    const bValue = b[sortField as keyof BillPaymentDetail];
    
    if (sortField === 'due_date' || sortField === 'invoice_date' || sortField === 'funds_received_date') {
      return sortDirection === 'asc'
        ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
        : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    return 0;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'MM/dd/yyyy');
  };

  const getFundsStatusBadge = (status: BillPaymentDetail['funds_requested_status']) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'Pending', icon: Clock, color: 'text-muted-foreground' },
      requested: { variant: 'default' as const, label: 'Requested', icon: AlertCircle, color: 'text-amber-600' },
      received: { variant: 'default' as const, label: 'Received', icon: CheckCircle, color: 'text-green-600' },
    };
    
    const config = variants[status];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1.5">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  const getPaymentExecutionBadge = (status: BillPaymentDetail['payment_execution_status']) => {
    const variants = {
      pending: { variant: 'secondary' as const, label: 'Pending', icon: Clock, color: 'text-muted-foreground' },
      executed: { variant: 'default' as const, label: 'Executed', icon: CheckCircle, color: 'text-blue-600' },
      failed: { variant: 'destructive' as const, label: 'Failed', icon: AlertCircle, color: 'text-red-600' },
      reconciled: { variant: 'default' as const, label: 'Reconciled', icon: CheckCircle, color: 'text-green-600' },
    };
    
    const config = variants[status];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1.5">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {config.label}
      </Badge>
    );
  };

  const paymentTaskId = `PT-${stage.toUpperCase().slice(0, 2)}-0001`;
  const processingDate = '2025-12-15';

  const SortableHeader = ({ field, children, icon }: { field: string; children: React.ReactNode; icon?: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 transition-colors" 
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2 font-semibold">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {children}
        <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
      </div>
    </TableHead>
  );

  const canExport = stage === 'payment-ready' || stage === 'funds-requested' || stage === 'funds-received' || stage === 'payment-initiated';
  const canImport = stage === 'funds-received' || stage === 'paid';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Receipt className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-primary">
                  Payment Task: {paymentTaskId}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Stage: {stage.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} • Created: {formatDate(processingDate)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {canExport && (
                <Button variant="outline" size="sm" className="gap-2">
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
              )}
              {canImport && (
                <Button variant="outline" size="sm" className="gap-2">
                  <Upload className="h-4 w-4" />
                  Import CSV
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mt-6">
          <div className="border-b border-border">
            <TabsList className="w-full h-auto p-0 bg-transparent justify-start overflow-x-auto flex-nowrap gap-1">
              <TabsTrigger 
                value="bills" 
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
              >
                <FileText className="h-4 w-4" />
                Bills ({billDetails.length})
              </TabsTrigger>
              <TabsTrigger 
                value="status" 
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
              >
                <CheckCircle className="h-4 w-4" />
                Payment Status
              </TabsTrigger>
              <TabsTrigger 
                value="reconciliation" 
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
              >
                <RefreshCw className="h-4 w-4" />
                Reconciliation
              </TabsTrigger>
              <TabsTrigger 
                value="audit" 
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all whitespace-nowrap"
              >
                <History className="h-4 w-4" />
                Audit Trail
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="bills" className="mt-6">
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow>
                    <SortableHeader field="bill_id" icon={<FileText className="h-4 w-4" />}>
                      Bill ID
                    </SortableHeader>
                    <SortableHeader field="customer_name" icon={<Users className="h-4 w-4" />}>
                      Customer
                    </SortableHeader>
                    <SortableHeader field="account_number" icon={<Home className="h-4 w-4" />}>
                      Service Account
                    </SortableHeader>
                    <SortableHeader field="commodity" icon={<Zap className="h-4 w-4" />}>
                      Commodity
                    </SortableHeader>
                    <SortableHeader field="vendor_name" icon={<Building className="h-4 w-4" />}>
                      <div className="flex items-center gap-1">
                        Vendor
                        <Info className="h-3 w-3 text-primary" />
                      </div>
                    </SortableHeader>
                    <SortableHeader field="billing_id" icon={<CreditCard className="h-4 w-4" />}>
                      Billing ID
                    </SortableHeader>
                    <SortableHeader field="due_date" icon={<Calendar className="h-4 w-4" />}>
                      Due Date
                    </SortableHeader>
                    <SortableHeader field="amount_due" icon={<DollarSign className="h-4 w-4" />}>
                      Amount Due
                    </SortableHeader>
                    <SortableHeader field="fees" icon={<DollarSign className="h-4 w-4" />}>
                      Fees
                    </SortableHeader>
                    <SortableHeader field="amount_paid" icon={<DollarSign className="h-4 w-4" />}>
                      Amount Paid
                    </SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center py-12">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="h-12 w-12 text-muted-foreground/50" />
                          <p className="text-muted-foreground font-medium">No bills found in this payment task</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedBills.map((bill, index) => (
                      <TableRow
                        key={bill.id}
                        className={index % 2 === 0 ? 'bg-muted/30' : ''}
                      >
                        <TableCell>
                          <button className="text-primary hover:underline font-medium font-mono">
                            {bill.bill_id}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Users className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">{bill.customer_name || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Home className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm font-mono">{bill.account_number || bill.billing_id}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Zap className="h-3.5 w-3.5 text-muted-foreground" />
                            <Badge variant="outline" className="capitalize">
                              {bill.commodity || 'N/A'}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 max-w-[200px]">
                            <Building className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm leading-tight">{bill.vendor_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 max-w-[150px]">
                            <CreditCard className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm font-mono leading-tight">{bill.billing_id}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-sm">{formatDate(bill.due_date)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-semibold">{formatCurrency(bill.amount_due)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{formatCurrency(bill.fees)}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-primary" />
                            <span className="font-semibold text-primary">{formatCurrency(bill.amount_paid)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="status" className="mt-6">
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow>
                    <SortableHeader field="bill_id" icon={<FileText className="h-4 w-4" />}>
                      Bill ID
                    </SortableHeader>
                    <SortableHeader field="customer_name" icon={<Users className="h-4 w-4" />}>
                      Customer
                    </SortableHeader>
                    <TableHead>
                      <div className="flex items-center gap-2 font-semibold">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        Funds Status
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-2 font-semibold">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        Funds Received
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-2 font-semibold">
                        <CheckCircle className="h-4 w-4 text-muted-foreground" />
                        Payment Status
                      </div>
                    </TableHead>
                    <TableHead>
                      <div className="flex items-center gap-2 font-semibold">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        Amount
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBills.map((bill, index) => (
                    <TableRow key={bill.id} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                      <TableCell>
                        <span className="font-mono font-medium">{bill.bill_id}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{bill.customer_name || 'N/A'}</span>
                      </TableCell>
                      <TableCell>
                        {getFundsStatusBadge(bill.funds_requested_status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm">{formatDate(bill.funds_received_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getPaymentExecutionBadge(bill.payment_execution_status)}
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold">{formatCurrency(bill.amount_due)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="reconciliation" className="mt-6">
            <div className="space-y-4">
              {/* Reconciliation Summary */}
              <Card className="border-l-4 border-l-emerald-500">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <RefreshCw className="h-5 w-5 text-emerald-600" />
                        Reconciliation Overview
                      </CardTitle>
                      <CardDescription>Track and manage payment reconciliation status</CardDescription>
                    </div>
                    {stage === 'payment-initiated' && (
                      <Button className="gap-2">
                        <Upload className="h-4 w-4" />
                        Import Bank Confirmation
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="space-y-1 p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">Total Bills</p>
                      <p className="text-2xl font-bold">{billDetails.length}</p>
                    </div>
                    <div className="space-y-1 p-4 bg-green-50 dark:bg-green-950/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">Reconciled</p>
                      <p className="text-2xl font-bold text-green-600">
                        {billDetails.filter(b => b.payment_execution_status === 'reconciled').length}
                      </p>
                    </div>
                    <div className="space-y-1 p-4 bg-blue-50 dark:bg-blue-950/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">Executed</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {billDetails.filter(b => b.payment_execution_status === 'executed').length}
                      </p>
                    </div>
                    <div className="space-y-1 p-4 bg-amber-50 dark:bg-amber-950/10 rounded-lg">
                      <p className="text-sm text-muted-foreground">Pending</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {billDetails.filter(b => b.payment_execution_status === 'pending').length}
                      </p>
                    </div>
                  </div>

                  {/* Property-wise Grouping */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin className="h-4 w-4 text-primary" />
                      <h4 className="font-semibold">Reconciliation by Property</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {['Property A', 'Property B', 'Property C'].map((property, idx) => {
                        const propertyBills = billDetails.filter((_, i) => i % 3 === idx);
                        const reconciled = propertyBills.filter(b => b.payment_execution_status === 'reconciled').length;
                        const total = propertyBills.length;
                        const amount = propertyBills.reduce((sum, b) => sum + b.amount_due, 0);
                        return (
                          <Card key={property} className="border-l-4 border-l-primary/50">
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-base">{property}</CardTitle>
                                <Badge variant={reconciled === total ? 'default' : 'secondary'}>
                                  {reconciled}/{total} Reconciled
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Bills</span>
                                  <span className="font-semibold">{total}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Total Amount</span>
                                  <span className="font-semibold">{formatCurrency(amount)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-muted-foreground">Reconciliation Rate</span>
                                  <span className="font-semibold text-green-600">
                                    {total > 0 ? Math.round((reconciled / total) * 100) : 0}%
                                  </span>
                                </div>
                                <div className="mt-3">
                                  <div className="flex items-center justify-between text-xs mb-1">
                                    <span>Progress</span>
                                    <span>{total > 0 ? Math.round((reconciled / total) * 100) : 0}%</span>
                                  </div>
                                  <Progress 
                                    value={total > 0 ? (reconciled / total) * 100 : 0} 
                                    className="h-2"
                                  />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>

                  {/* Quick Reconciliation Actions */}
                  {stage === 'payment-initiated' && (
                    <Card className="mt-4 border-l-4 border-l-purple-500">
                      <CardHeader>
                        <CardTitle className="text-base">Bulk Reconciliation Actions</CardTitle>
                        <CardDescription>Import bank confirmation file to reconcile all bills in this payment task</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2">
                          <Button className="gap-2">
                            <Upload className="h-4 w-4" />
                            Import Bank Confirmation CSV
                          </Button>
                          <Button variant="outline" className="gap-2">
                            <Download className="h-4 w-4" />
                            Download Template
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Import a CSV file with bank transaction details to automatically reconcile all bills. 
                          File must include Payment Task ID and Bill ID for matching.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Payment Task Events & Audit Trail</CardTitle>
                  <CardDescription>Complete event history and reconciliation status for this payment task</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4 p-4 border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/10 rounded-lg">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold">Payment Task Created</p>
                          <span className="text-sm text-muted-foreground">{formatDate(processingDate)}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">Created by: System User</p>
                        <p className="text-sm font-mono text-muted-foreground">Payment Task ID: {paymentTaskId}</p>
                        <Badge variant="outline" className="mt-2">Status: Created</Badge>
                      </div>
                    </div>
                    {stage !== 'payment-ready' && (
                      <div className="flex items-start gap-4 p-4 border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/10 rounded-lg">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                          <Download className="h-4 w-4 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold">Funds Requested CSV Exported</p>
                            <span className="text-sm text-muted-foreground">{formatDate(processingDate)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">Exported by: System User</p>
                          <p className="text-sm font-mono text-muted-foreground">File: funds_requested_{paymentTaskId}.csv</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">Source: Export</Badge>
                            <Badge variant="default">Bills: {billDetails.length}</Badge>
                          </div>
                        </div>
                      </div>
                    )}
                    {(stage === 'funds-received' || stage === 'payment-initiated' || stage === 'paid') && (
                      <>
                        <div className="flex items-start gap-4 p-4 border-l-4 border-l-green-500 bg-green-50/50 dark:bg-green-950/10 rounded-lg">
                          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                            <Upload className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <p className="font-semibold">Funds Received CSV Imported</p>
                              <span className="text-sm text-muted-foreground">{formatDate('2025-12-10')}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">Imported by: System User</p>
                            <p className="text-sm font-mono text-muted-foreground">File: funds_received_{paymentTaskId}.csv</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">Source: Import</Badge>
                              <Badge variant="default" className="bg-green-600">Status: Funds Confirmed</Badge>
                            </div>
                          </div>
                        </div>
                        {(stage === 'payment-initiated' || stage === 'paid') && (
                          <div className="flex items-start gap-4 p-4 border-l-4 border-l-purple-500 bg-purple-50/50 dark:bg-purple-950/10 rounded-lg">
                            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                              <Download className="h-4 w-4 text-purple-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold">Vendor Payment File Exported</p>
                                <span className="text-sm text-muted-foreground">{formatDate('2025-12-11')}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">Exported by: System User</p>
                              <p className="text-sm font-mono text-muted-foreground">File: vendor_payment_{paymentTaskId}.csv</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline">Source: Export</Badge>
                                <Badge variant="default" className="bg-purple-600">Status: Payment Initiated</Badge>
                              </div>
                            </div>
                          </div>
                        )}
                        {stage === 'paid' && (
                          <div className="flex items-start gap-4 p-4 border-l-4 border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/10 rounded-lg">
                            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                              <Upload className="h-4 w-4 text-emerald-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold">Bank Confirmation CSV Imported</p>
                                <span className="text-sm text-muted-foreground">{formatDate('2025-12-12')}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">Imported by: System User</p>
                              <p className="text-sm font-mono text-muted-foreground">File: bank_confirmation_{paymentTaskId}.csv</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline">Source: Bank Import</Badge>
                                <Badge variant="default" className="bg-emerald-600">Status: Reconciled</Badge>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Reconciliation Summary */}
              {stage === 'paid' && (
                <Card className="border-l-4 border-l-emerald-500">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      Reconciliation Summary
                    </CardTitle>
                    <CardDescription>Payment task reconciliation status and details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Total Bills</p>
                        <p className="text-2xl font-bold">{billDetails.length}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Reconciled</p>
                        <p className="text-2xl font-bold text-green-600">{billDetails.filter(b => b.payment_execution_status === 'reconciled').length}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Total Amount</p>
                        <p className="text-2xl font-bold">{formatCurrency(billDetails.reduce((sum, b) => sum + b.amount_due, 0))}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Reconciliation Rate</p>
                        <p className="text-2xl font-bold text-emerald-600">100%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end mt-6 pt-4 border-t">
          <Button onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

